import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import SEO from "../components/SEO";
import HorizontalScroll from "../components/HorizontalScroll";
import api from "../api";
import { COMPARISON_ROWS, comparisonProduct, productPriceLabel, productSaleLabel, productSlug } from "../utils/comparisonProducts";

const catalogParams = { include_rental: true, representative: true, fields: "compare", limit: 24 };

function ProductSelector({ onSelect, selectedIds }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      api.get("/api/store/products", { params: { ...catalogParams, q: query.trim() }, signal: controller.signal })
        .then(({ data }) => setProducts((data.products || []).map(comparisonProduct)))
        .catch(err => { if (err.code !== "ERR_CANCELED") setError("제품 목록을 불러오지 못했습니다."); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 180);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, query, retry]);

  return <div className="dept-compare-picker relative">
    <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
      className="w-full min-h-[230px] border-2 border-dashed border-gray-300 bg-white flex flex-col items-center justify-center gap-3 text-gray-600 hover:border-blue-700">
      <span aria-hidden="true" className="text-4xl">＋</span><span>제품 추가</span>
    </button>
    {open && <div className="dept-compare-picker__panel absolute top-0 inset-x-0 z-30 bg-white border border-gray-300 shadow-xl">
      <div className="flex gap-2 p-3 border-b">
        <input autoFocus aria-label="비교할 제품 검색" placeholder="모델명·토너명 검색" value={query} onChange={e => setQuery(e.target.value)} className="min-w-0 flex-1 border px-2 py-2 text-sm" />
        <button type="button" aria-label="제품 선택 닫기" onClick={() => setOpen(false)} className="px-2">✕</button>
      </div>
      <div className="max-h-[360px] overflow-y-auto" aria-live="polite">
        {loading ? <p className="p-5 text-sm">제품을 불러오는 중…</p> : error ? <div className="p-4 text-sm"><p>{error}</p><button onClick={() => setRetry(n => n + 1)} className="mt-2 underline">다시 시도</button></div> : <>
          {products.filter(p => !selectedIds.has(p.id)).map(p => <button key={p.id} type="button" onClick={() => { onSelect(p); setOpen(false); }} className="flex w-full items-center gap-3 p-3 text-left hover:bg-blue-50 border-b">
            <img src={p.image_url} alt="" className="w-12 h-12 object-contain" />
            <span className="min-w-0"><span className="block text-sm font-semibold">{p.name}</span><span className="block text-xs text-gray-600">{productSaleLabel(p)} · {productPriceLabel(p)}{p.stock_status === "soldout" ? " · 품절" : ""}</span></span>
          </button>)}
          {!products.some(p => !selectedIds.has(p.id)) && <p className="p-5 text-sm text-gray-600">추가할 제품이 없습니다. 다른 모델명으로 검색하세요.</p>}
        </>}
      </div>
    </div>}
  </div>;
}

async function refreshItem(item) {
  let slug = productSlug(item);
  if (!slug) {
    // Restore saved legacy items by an exact name; a fuzzy match can pick another SKU.
    const { data } = await api.get("/api/store/products", { params: { ...catalogParams, q: item.name || item.model_name } });
    const exact = (data.products || []).filter(p => p.name === item.name || (item.model_name && p.model_name === item.model_name));
    if (exact.length !== 1) throw new Error("상품 연결 확인 필요");
    slug = exact[0].slug;
  }
  const { data } = await api.get(`/api/product-pages/${encodeURIComponent(slug)}`);
  return comparisonProduct(data);
}

export default function ComparePage() {
  const { compareItems, removeFromCompare, clearCompare, addToCart, addToCompare, replaceCompareItems } = useShop();
  const navigate = useNavigate();
  const initialItems = useRef(compareItems);
  const [refreshing, setRefreshing] = useState(initialItems.current.length > 0);
  const [toast, setToast] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setRefreshing(initialItems.current.length > 0);
    Promise.all(initialItems.current.map(item => refreshItem(item).catch(() => ({ ...item, unavailable: true }))))
      .then(items => {
        if (!cancelled) {
          replaceCompareItems(items.filter((item, index) => items.findIndex(p => p._product_type === item._product_type && p._product_id === item._product_id) === index));
          setRefreshing(false);
        }
      });
    return () => { cancelled = true; };
  }, [replaceCompareItems, retry]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(timer);
  }, [toast]);
  const addCart = async item => {
    if (busyId !== null) return;
    setBusyId(item.id);
    try {
      const result = await addToCart("page", item.id);
      if (result?.needLogin) navigate("/login", { state: { from: "/compare" } });
      else setToast(result?.error || "장바구니에 추가했습니다.");
    } finally { setBusyId(null); }
  };
  const rows = COMPARISON_ROWS.filter(row => !row.optional || compareItems.some(item => !item.unavailable && row.value(item)));
  return <div className="dept-compare-page bg-[#fbfbfd] min-h-screen pb-16">
    <SEO title="제품 비교" path="/compare" />
    <section className="dept-legacy-hero"><div className="max-w-5xl mx-auto px-6 py-10"><h1>제품 비교</h1><p>구매 가격과 월 렌탈료, 제품 사양을 나란히 확인하세요.</p></div></section>
    <div className="max-w-[1200px] mx-auto px-4">
      {toast && <p role="status" className="p-4 my-4 border bg-white">{toast}</p>}
      {refreshing ? <p role="status" className="py-8">최신 상품 정보를 확인하고 있습니다…</p> : <>
        <div className="flex justify-between items-center py-5"><span>{compareItems.length}개 제품 비교 중</span>{compareItems.length > 0 && <button type="button" className="underline text-sm" onClick={clearCompare}>전체 삭제</button>}</div>
        {compareItems.some(p => p.unavailable) && <div role="alert" className="p-4 mb-4 border border-amber-300 bg-amber-50">일부 상품의 최신 정보를 확인하지 못했습니다. <button className="underline" onClick={() => { initialItems.current = compareItems; setRetry(n => n + 1); }}>다시 확인</button></div>}
        <div className="dept-compare-cards grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" }}>
          {compareItems.map(item => <article key={`${item._product_type}_${item._product_id}`} className="bg-white border p-4 relative">
            <button type="button" aria-label={`${item.name || item.model_name} 비교에서 제거`} onClick={() => removeFromCompare(item._product_type, item._product_id)} className="absolute right-2 top-2 w-9 h-9 bg-gray-100">✕</button>
            <img src={item.image_url} alt="" className="w-full h-40 object-contain my-4" />
            <p className="text-sm text-gray-600">{item.unavailable ? "확인 필요" : productSaleLabel(item)}</p>
            <h2 className="font-bold mt-2 min-h-[48px]">{item.name || item.model_name}</h2>
            {!item.unavailable && <><p className="font-bold my-3">{productPriceLabel(item)}</p>
              {item.stock_status === "soldout" ? <p className="p-3 text-red-700 bg-red-50">품절 · 재입고 대기</p> : item.sale_type === "rental" || item.sale_type === "both" || !Number(item.price) ? <Link to={`/p/${item.slug}`} className="block text-center bg-emerald-700 text-white p-3">제품 조건·견적 보기</Link> : <button type="button" disabled={busyId !== null} onClick={() => addCart(item)} className="w-full bg-blue-700 text-white p-3 disabled:opacity-50">{busyId === item.id ? "담는 중…" : "장바구니"}</button>}
              <Link to={`/p/${item.slug}`} className="block mt-3 text-center underline text-blue-800">자세히 보기</Link>
            </>}
          </article>)}
          {compareItems.length < 4 && <ProductSelector onSelect={addToCompare} selectedIds={new Set(compareItems.filter(p => p._product_type === "page").map(p => p.id))} />}
        </div>
        {compareItems.length > 0 && <HorizontalScroll label="제품 사양 비교표" className="mt-8"><div className="dept-compare-table overflow-x-auto bg-white border" tabIndex={0} aria-label="제품 사양 비교표">
          <table className="w-full text-sm text-left"><caption className="p-4 text-left font-semibold">제품 사양 비교 · 빈 항목은 상세페이지에서 확인해 주세요.<span className="dept-compare-scroll-hint">표를 좌우로 움직여 다른 제품을 비교하세요.</span></caption><thead><tr><th className="p-4 min-w-[120px]">항목</th>{compareItems.map(item => <th key={`${item._product_type}_${item._product_id}`} className="p-4 min-w-[200px]">{item.name || item.model_name}</th>)}</tr></thead>
            <tbody>{rows.map(row => <tr key={row.label} className="border-t"><th scope="row" className="p-4 text-gray-600">{row.label}</th>{compareItems.map(item => <td key={`${item._product_type}_${item._product_id}`} className="p-4">{item.unavailable ? "확인 필요" : row.value(item) || "상세페이지 확인"}</td>)}</tr>)}</tbody>
          </table>
        </div></HorizontalScroll>}
      </>}
    </div>
  </div>;
}
